/**
 * The app's `useMutation` (#1174, owner Q-291 a): TanStack's `useMutation`,
 * plus `meta.feedbackLayer` naming the dialog layer the calling component
 * sits in and that dialog's opening, both read at render.
 *
 * `routeMutationError` (the MutationCache `onError`) uses it to show a 5xx or
 * transport failure ONCE, in that dialog's band while the opening the person
 * acted in is still open, else on the global banner (see
 * `feedbackLayerMeta.ts`). TanStack copies `meta` onto the Mutation at
 * `mutate()` time, so the capture survives the component unmounting.
 *
 * It reads `useDialogLayer()`, not `useFeedbackZone()`: outside any dialog the
 * layer is null, and the failure keeps its global path.
 *
 * A caller that sets `meta.feedbackLayer` itself (e.g. `CALLER_REPORTS`) keeps
 * it; every other meta key is passed through unchanged.
 */
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- the wrapper itself: the one production site that calls TanStack's useMutation (#1174)
import { useMutation as useTanStackMutation } from '@tanstack/react-query';
import { useId, useMemo, useRef } from 'react';
import {
  type DefaultError,
  type QueryClient,
  type UseMutationOptions,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  useDialogLayer,
  useDialogLayerOpening,
} from '@/components/dialogs/StyledDialog/DialogLayerContext';
import type { AppMutationMeta } from '@/api/feedbackLayerMeta';
import { useFeedbackStore } from '@/store/useFeedbackStore';
import { runWithFeedbackSource } from '@/api/feedbackSourceScope';

/** Non-TanStack options this wrapper takes and strips. */
export interface FeedbackMutationOptions {
  /**
   * The action's key (#1628). A failure shown for this mutation carries it, and
   * a later success clears it. Default: this hook instance's own `useId()`, so
   * one instance is one action. Must be stable across renders (a `useId()` or a
   * constant), never a per-attempt value.
   */
  feedbackSource?: string;
}

/** The wrapper's result: TanStack's, plus the action's key for a caller that reports in a `catch` (#1628-b). */
export type AppMutationResult<TData, TError, TVariables, TOnMutateResult> = UseMutationResult<
  TData,
  TError,
  TVariables,
  TOnMutateResult
> & {
  feedbackSource: string;
  /** The key one run (with these variables) shows and clears under: `feedbackSource` unless `feedbackKeyOf` is set (#1628-d4). */
  feedbackSourceFor: (variables: TVariables) => string;
};

type AnyFn = (...args: never[]) => unknown;

/** `fn` wrapped so it runs inside the mutation's scope; `at` is the index of `variables` in its arguments. */
function scoped<F extends AnyFn | undefined>(keyFor: (variables: never) => string, at: number, fn: F): F {
  if (!fn) return fn;
  return ((...args: never[]) =>
    runWithFeedbackSource(keyFor(args[at] as never), () => (fn as AnyFn)(...args))) as unknown as F;
}

/**
 * Per-call callbacks run later, from `Mutation#dispatch`, not inside `mutate`:
 * each callback is wrapped, never the call (#1628-b).
 */
function scopeCallOptions<O extends object | undefined>(keyFor: (variables: never) => string, o: O): O {
  if (!o) return o;
  const c = o as { onError?: AnyFn; onSuccess?: AnyFn; onSettled?: AnyFn };
  return {
    ...o,
    onError: scoped(keyFor, 1, c.onError),
    onSuccess: scoped(keyFor, 1, c.onSuccess),
    onSettled: scoped(keyFor, 2, c.onSettled),
  } as O;
}

export function useMutation<
  TData = unknown,
  TError = DefaultError,
  TVariables = void,
  TOnMutateResult = unknown,
>(
  options: UseMutationOptions<TData, TError, TVariables, TOnMutateResult> &
    FeedbackMutationOptions & {
      /**
       * One binding, many rows (#1628-d4): the run's key is `${key}:${feedbackKeyOf(variables)}`, so
       * a sibling row's success neither clears nor a sibling row's push replaces this row's failure.
       * Return a stable id from the variables (a user id, an alert id, a `contactMethodKey`), never a
       * per-attempt value. Unset: every run uses `key` (today's behaviour).
       */
      feedbackKeyOf?: (variables: TVariables) => string | number;
    },
  queryClient?: QueryClient,
): AppMutationResult<TData, TError, TVariables, TOnMutateResult> {
  const { feedbackSource, feedbackKeyOf, ...tanStackOptions } = options;
  const ownKey = useId();
  const key = feedbackSource ?? ownKey;
  // Read through a ref: the option is usually an inline arrow, and a run started before a re-render must
  // still compute the same key for its success clear and its routed failure.
  const keyOfRef = useRef(feedbackKeyOf);
  keyOfRef.current = feedbackKeyOf;
  const hasKeyOf = feedbackKeyOf !== undefined;
  const keyFor = useMemo(
    () => (variables: never): string => {
      const of = keyOfRef.current;
      return of ? `${key}:${of(variables)}` : key;
    },
    [key],
  );
  const layer = useDialogLayer();
  const opening = useDialogLayerOpening();
  const callerMeta = tanStackOptions.meta;
  const callerOwnsLayer = !!callerMeta && 'feedbackLayer' in callerMeta;
  const meta: AppMutationMeta = callerOwnsLayer
    ? callerMeta
    : { ...callerMeta, feedbackLayer: { layer, opening, source: key, ...(hasKeyOf && { sourceOf: keyFor }) } };
  // Caller mode (`CALLER_REPORTS`) gets no ambient scope: the caller reports
  // through `presentCallerMutationError` with `feedbackSource` (#1628-c2), and
  // a success below clears that key in every mode.
  const scope = <F extends AnyFn | undefined>(at: number, fn: F): F => (callerOwnsLayer ? fn : scoped(keyFor, at, fn));
  const userOnSuccess = scope(1, tanStackOptions.onSuccess);
  const result = useTanStackMutation(
    {
      ...tanStackOptions,
      meta,
      onError: scope(1, tanStackOptions.onError),
      onSettled: scope(2, tanStackOptions.onSettled),
      // A success clears this action's earlier failure, on the surface the
      // failure was shown on (#1628), before the caller's follow-up runs. The
      // failure may have been reported to another zone than the captured layer
      // (a page zone from inside a dialog child), so the global store is
      // cleared too: keys are unique, a clear where nothing carries it is a
      // no-op. The caller's result is returned so TanStack still awaits it (#869).
      onSuccess: (data, variables, onMutateResult, context) => {
        const runKey = keyFor(variables as never);
        layer?.clearSource(runKey);
        useFeedbackStore.getState().clearSource(runKey);
        return userOnSuccess?.(data, variables, onMutateResult, context);
      },
    },
    queryClient,
  );
  const { mutate, mutateAsync } = result;
  const scopedMutate = useMemo(
    () =>
      callerOwnsLayer
        ? mutate
        : (((variables, o) => mutate(variables, scopeCallOptions(keyFor, o))) as typeof mutate),
    [mutate, keyFor, callerOwnsLayer],
  );
  const scopedMutateAsync = useMemo(
    () =>
      callerOwnsLayer
        ? mutateAsync
        : (((variables, o) => mutateAsync(variables, scopeCallOptions(keyFor, o))) as typeof mutateAsync),
    [mutateAsync, keyFor, callerOwnsLayer],
  );
  return {
    ...result,
    mutate: scopedMutate,
    mutateAsync: scopedMutateAsync,
    feedbackSource: key,
    feedbackSourceFor: keyFor as (variables: TVariables) => string,
  };
}
